/**
 * 별채대화 조립 (시안 03 README 5.4·6장 S1). 서버의 말(room_messages, 매일 콘텐츠 카드 말 포함)과
 * 아직 서버에 닿지 않은 내 말을 시각순으로 섞고 날짜 구분선을 끼웁니다. 최신이 아래입니다.
 * 카드 말(kind daily_card)은 그 글(itemsById)로 카드를 그립니다. 글이 아직 없으면 그리지 않습니다.
 *
 * - 하루의 첫 구분선은 "아침 · 10월 13일 화", 같은 날 안에서 때가 바뀌면 "점심", "저녁", "밤".
 *   단골이 된 날은 "처음 들어온 날 · 별채" (시안 04 D3a)
 * - 같은 멤버가 이어 말하면 아바타·이름을 빼고(showHead=false) 위 간격 4
 * - 받아치기는 받는 말이 바로 위가 아닐 때만 "↳ {멤버}: {원문 앞부분}" 인용(첫날 입장 장면은 바로 위라도)
 * 시각은 서울 기준입니다(seoulParts).
 */
import { seoulParts } from '../board/boardFeed.js';

const QUOTE_LENGTH = 18;

/** 별채대화의 때: 아침(5~11시) · 점심(11~15시) · 저녁(15~21시) · 밤 */
export function roomPartOf(hour) {
    if (hour >= 5 && hour < 11) {
        return '아침';
    }
    if (hour >= 11 && hour < 15) {
        return '점심';
    }
    if (hour >= 15 && hour < 21) {
        return '저녁';
    }
    return '밤';
}

function dateKeyOf(parts) {
    return `${parts.year}-${parts.month}-${parts.day}`;
}

export function excerpt(text, length = QUOTE_LENGTH) {
    const chars = Array.from(
        String(text ?? '')
            .replace(/\s+/g, ' ')
            .trim()
    );
    return chars.length > length ? `${chars.slice(0, length).join('')}…` : chars.join('');
}

/** 시스템 줄 본문이 JSON 이면 그 장면 */
export function systemSceneOf(body) {
    if (typeof body !== 'string' || !body.startsWith('{')) {
        return null;
    }
    try {
        return JSON.parse(body);
    } catch {
        return null;
    }
}

function entryOf(message, itemsById) {
    if (message.kind === 'daily_card') {
        const item = itemsById[message.dailyItemsId];
        return item ? { type: 'daily', item, message } : null;
    }
    if (message.kind === 'safety_card') {
        return { type: 'safety', message };
    }
    if (message.kind === 'worry_card') {
        return { type: 'worry', message };
    }
    // 「이모 수첩에 적었어요 · 고치기」 (D2a), 「{호칭} 님이 별채에 들어왔어요」 (D3a)
    if (message.kind === 'memo') {
        return message.memo ? { type: 'memo', message } : null;
    }
    if (message.kind === 'system') {
        // 멤버 명함 줄 · 「이모 수첩 보기 ›」 줄(첫 방문 2막 · 첫 주 이야기, C-21 · D76)은 JSON 본문
        const special = systemSceneOf(message.body);
        if (special?.type === 'member_cards' && Array.isArray(special.members)) {
            return { type: 'member_cards', message, members: special.members };
        }
        if (special?.type === 'memo_link') {
            return { type: 'memo_link', message };
        }
        return { type: 'system', message };
    }
    if (message.sender === 'user') {
        return { type: 'mine', message };
    }
    return { type: 'member', message };
}

/**
 * @param {object} options
 * @param {object[]} options.messages  서버의 말(시간순)
 * @param {Record<number, object>} [options.itemsById]  카드 말의 글(상회칠판과 같은 글)
 * @param {object[]} [options.outbox]  아직 저장되지 않은 내 말 { clientKey, body, status, createdAt }
 * @param {string|null} [options.joinedDate]  단골이 된 서울 날짜 'YYYY-M-D' (처음 온 날 구분선)
 */
export function buildRoomFeed({ messages, itemsById = {}, outbox = [], joinedDate = null }) {
    const entries = [
        ...messages.flatMap((message) => {
            const entry = entryOf(message, itemsById);
            if (!entry) {
                return [];
            }
            return [
                { ...entry, at: message.createdAt, order: message.roomMessagesId, key: `m${message.roomMessagesId}` },
            ];
        }),
        ...outbox.map((pending) => ({
            type: 'outbox',
            pending,
            at: pending.createdAt,
            order: Number.MAX_SAFE_INTEGER,
            key: `o${pending.clientKey}`,
        })),
    ].sort((a, b) => new Date(a.at) - new Date(b.at) || a.order - b.order);

    const byId = new Map(messages.map((message) => [message.roomMessagesId, message]));
    const rows = [];
    let currentDate = null;
    let currentPart = null;
    let previous = null;
    for (const entry of entries) {
        const parts = seoulParts(entry.at);
        const dateKey = dateKeyOf(parts);
        const part = roomPartOf(parts.hour);
        if (dateKey !== currentDate) {
            const label =
                dateKey === joinedDate
                    ? '처음 들어온 날 · 별채'
                    : `${part} · ${parts.month}월 ${parts.day}일 ${parts.weekday}`;
            rows.push({ type: 'divider', key: `d${dateKey}`, label });
            currentDate = dateKey;
            currentPart = part;
            previous = null;
        } else if (part !== currentPart) {
            rows.push({ type: 'divider', key: `d${dateKey}-${part}`, label: part });
            currentPart = part;
            previous = null;
        }

        if (entry.type === 'member') {
            const { message } = entry;
            const sameSpeaker = previous?.type === 'member' && previous.message.memberKey === message.memberKey;
            let quote = null;
            const target = message.replyToRoomMessagesId ? byId.get(message.replyToRoomMessagesId) : null;
            const targetIsJustAbove =
                previous?.type === 'member' && previous.message.roomMessagesId === target?.roomMessagesId;
            // 첫날 입장 장면(첫 판 받아치기 · 맞수 주고받기)은 바로 위라도 인용 줄을 답니다 (시안 04 D3a · D3b)
            const entranceDay = dateKey === joinedDate;
            if (target && target.sender === 'member' && (!targetIsJustAbove || entranceDay)) {
                quote = { memberKey: target.memberKey, text: excerpt(target.body) };
            }
            rows.push({ ...entry, showHead: !sameSpeaker, quote });
        } else {
            rows.push(entry);
        }
        previous = entry;
    }
    return rows;
}

/** 아직 답하지 않은, 칩이 붙은 부르기가 있는지 (별채대화 탭 점). 「나머지 얘기도 듣기」는 부르기가 아닙니다 */
export function hasOpenHook(messages) {
    return messages.some(
        (message) =>
            message.hook &&
            !message.hook.answered &&
            !['worry', 'more_voices'].includes(message.hook.kind) &&
            message.kind === 'text'
    );
}

/** 대화 선택 패널의 별채대화 줄: 마지막 멤버 말 「오름: …」 */
export function roomPreviewOf(messages, nameOf) {
    const last = [...messages]
        .reverse()
        .find((message) => message.sender === 'member' && message.kind === 'text' && message.body);
    return last ? `${nameOf(last.memberKey)}: ${excerpt(last.body, 24)}` : '';
}

/**
 * 돌아왔을 때 보이는 것 (SPEC_CHANGE_v1.3 15.5, DESIGN_SPEC 6장, C-20b · D67)
 * - line(24시간 안): 마지막으로 본 말 아래 첫 새 항목 앞(그 앞 날짜 구분선보다 위)에 「여기까지 봤어요」 선
 * - fold(1~3일) · news(3일 넘게): 본 곳 아래부터 열 때 있던 마지막 말까지를 접힌 묶음 하나로. fold 는 위에 요약 줄,
 *   news 는 위에 이모 소식 카드. 펼치면(expanded) 그대로 보입니다. 연 뒤 새로 온 말은 접지 않습니다
 * @param {object[]} rows  buildRoomFeed 결과
 * @param {object|null} visit  서버 returnVisit
 * @param {{ expanded?: boolean, openedLastId?: number }} [options]
 */
export function applyReturnVisit(rows, visit, { expanded = false, openedLastId = Number.MAX_SAFE_INTEGER } = {}) {
    if (!visit || visit.mode === 'none') {
        return rows;
    }
    const lastSeen = visit.lastSeenRoomMessagesId;
    const isNew = (row) =>
        row.type !== 'divider' && row.type !== 'outbox' && (row.message?.roomMessagesId ?? 0) > lastSeen;
    const first = rows.findIndex(isNew);
    if (first === -1) {
        return rows;
    }
    // 새 항목 바로 앞의 날짜 구분선들은 선 · 묶음 아래로
    let start = first;
    while (start > 0 && rows[start - 1].type === 'divider') {
        start -= 1;
    }
    if (visit.mode === 'line') {
        return [...rows.slice(0, start), { type: 'seenLine', key: 'seen-line' }, ...rows.slice(start)];
    }
    if (expanded) {
        return rows;
    }
    // 열 때 있던 말까지 접습니다(그 뒤 새로 온 말 · 내 말은 그대로)
    let end = start;
    while (end < rows.length) {
        const row = rows[end];
        const id = row.message?.roomMessagesId ?? 0;
        if (row.type === 'outbox' || (row.type !== 'divider' && id > openedLastId)) {
            break;
        }
        end += 1;
    }
    const folded = rows.slice(start, end).filter((row) => row.type !== 'divider');
    const aboutIds = new Set(visit.aboutUserIds ?? []);
    let top = [];
    if (visit.mode === 'news' && visit.news) {
        top = [{ type: 'news', key: 'return-news', news: visit.news }];
    } else if (visit.summary?.length) {
        top = [{ type: 'summary', key: 'return-summary', lines: visit.summary }];
    }
    const fold = {
        type: 'fold',
        key: 'return-fold',
        count: folded.length,
        hasAboutUser: folded.some((row) => aboutIds.has(row.message?.roomMessagesId)),
    };
    return [...rows.slice(0, start), ...top, fold, ...rows.slice(end)];
}
